import React from 'react'
import axios from 'axios'
import { useRouter } from 'next/router'

import { EXCLUDE_GSI_CLIENT_PATHS } from 'configs/routes'

import type { AxiosResponse } from 'axios'
import type { GoogleTokens, GoogleTokenInfo } from 'types/auth'

export interface GoogleAuthorizeResponse {
  readonly accessToken?: string
  readonly idToken?: string
  readonly scope?: string
  readonly expiresIn?: string
  readonly code?: string
  readonly emailDomain?: string
  readonly firstIssuedAt?: string
  readonly expiresAt?: string
}

export interface GoogleAuthorizeProps {
  readonly clientId: string
  readonly scope: string
  readonly cookiePolicy?: string
  readonly loginHint?: string
  readonly hostedDomain?: string
  readonly prompt?: string
  readonly responseType?: string
  readonly includeGrantedScopes?: boolean
}

export interface UseGoogleAuthorizeProps extends GoogleAuthorizeProps {
  readonly onSuccess?: (response: GoogleAuthorizeResponse) => void
  readonly onFailure?: (error: any) => void
  readonly onScriptLoadFailure?: (error: any) => void
  readonly jsSrc?: string
}

export interface UseGoogleAuthorizeResponse {
  authorize: () => void
  loaded: boolean
}

export interface ResponseSuccessGoogleExchangeToken {
  tokens: GoogleTokens
  tokenInfo: GoogleTokenInfo
}

export default function useGoogleAuthorize({
  onSuccess = () => {},
  onFailure = () => {},
  onScriptLoadFailure,
  clientId,
  cookiePolicy,
  loginHint,
  hostedDomain,
  scope,
  responseType,
  jsSrc = 'https://apis.google.com/js/api.js',
  includeGrantedScopes,
  prompt,
}: UseGoogleAuthorizeProps): UseGoogleAuthorizeResponse {
  const scriptId = 'google-authorize'
  const [loaded, setLoaded] = React.useState<boolean>(false)

  React.useEffect(() => {
    const onLoadFailure = onScriptLoadFailure || onFailure
    loadScript(
      scriptId,
      jsSrc,
      () => {
        setLoaded(true)
      },
      (err) => onLoadFailure(err)
    )

    return () => {
      removeScript(scriptId)
    }
    // eslint-disable-next-line
  }, [])

  const authorize = () => {
    if (loaded) {
      const params = {
        client_id: clientId,
        cookie_policy: cookiePolicy,
        login_hint: loginHint,
        hosted_domain: hostedDomain,
        include_granted_scopes: includeGrantedScopes,
        response_type: responseType,
        prompt,
        scope,
      }

      // @ts-expect-error
      window.gapi.load('auth2', () => {
        // @ts-expect-error
        window.gapi.auth2.authorize(params, (response) => {
          if (response.error) {
            onFailure(response.error)
          } else {
            const authResponse =
              params.response_type === 'code'
                ? { code: response.code, emailDomain: response.hd }
                : {
                    accessToken: response.access_token,
                    idToken: response.id_token,
                    scope: response.scope,
                    expiresIn: response.expires_in,
                    firstIssuedAt: response.first_issued_at,
                    expiresAt: response.expires_at,
                    emailDomain: response.hd,
                  }
            onSuccess(authResponse)
          }
        })
      })
    }
  }

  return {
    loaded,
    authorize,
  }
}

export function useGoogleIdentityServicesAuthorize({
  onSuccess = () => {},
  onFailure = () => {},
  onScriptLoadFailure,
  clientId,
  scope,
  jsSrc = 'https://accounts.google.com/gsi/client',
}: UseGoogleAuthorizeProps): UseGoogleAuthorizeResponse {
  const scriptId = 'gsi-client'
  const [loaded, setLoaded] = React.useState<boolean>(false)
  const [client, setClient] = React.useState<any>(undefined)
  const router = useRouter()

  const initClient = () => {
    // @ts-expect-error
    if (!window.google?.accounts) {
      onFailure('load_script_failed')
      return
    }

    // @ts-expect-error
    const newClient = window.google.accounts.oauth2.initCodeClient({
      client_id: clientId,
      scope,
      ux_mode: 'popup',
      callback: async (response) => {
        if (response.error) {
          onFailure(response.error)
        } else {
          const { code } = response

          try {
            const {
              data: { tokens, tokenInfo },
            } = await axios.post<
              any,
              AxiosResponse<ResponseSuccessGoogleExchangeToken>
            >('/api/auth/google/exchange-token', {
              code,
            })

            const authResponse = {
              code,
              accessToken: tokens.access_token,
              idToken: tokens.id_token,
              scope: tokens.scope,
              expiresIn: tokenInfo.exp,
              firstIssuedAt: tokenInfo.iat,
              expiresAt: tokens.expiry_date,
              emailDomain: tokenInfo.hd,
            }
            // @ts-expect-error
            onSuccess(authResponse)
          } catch ({ response }) {
            if (response?.data?.data?.error) {
              onFailure(response.data.data.error)
            } else {
              onFailure('login_failed')
            }
          }
        }
      },
    })
    setClient(newClient)
  }

  const initScript = () => {
    const onLoadFailure = onScriptLoadFailure || onFailure
    loadScript(
      scriptId,
      jsSrc,
      () => {
        initClient()
        setLoaded(true)
      },
      (err) => onLoadFailure(err)
    )
  }

  const authorize = () => {
    if (loaded) {
      client.requestCode()
    } else {
      initScript()
    }
  }

  // trigger init GSI Client script on non Landing page
  React.useEffect(() => {
    if (loaded || EXCLUDE_GSI_CLIENT_PATHS.includes(router.pathname)) {
      return
    }

    initScript()

    return () => {
      removeScript(scriptId)
    }
    // eslint-disable-next-line
  }, [loaded, router.pathname])

  return {
    loaded,
    authorize,
  }
}

function loadScript(
  id: string,
  jsSrc: string,
  onLoad: () => void,
  onError: (err: any) => void
) {
  const isLoaded =
    process.env.NEXT_PUBLIC_GOOGLE_IDENTITY_SERVICES_ENABLED === 'true'
      ? // @ts-expect-error
        !!window?.google?.accounts
      : // @ts-expect-error
        !!window?.gapi
  if (isLoaded) {
    onLoad()
  } else {
    const element = document.getElementsByTagName('script')[0]
    const fjs = element
    let js = element
    js = document.createElement('script')
    js.id = id
    js.src = jsSrc
    if (fjs && fjs.parentNode) {
      fjs.parentNode.insertBefore(js, fjs)
    } else {
      document.head.appendChild(js)
    }
    js.onerror = onError
    js.onload = onLoad
  }
}

function removeScript(id: string) {
  const element = document.getElementById(id)

  if (element) {
    element.parentNode.removeChild(element)
  }
}
